import { z } from "zod";

import { CaseType, CaseWorkflowStep } from "../case-core";
import {
  ChecklistGroupSchema,
  ChecklistItemSchema,
  RequirementKeySchema,
  SystemTaskKeySchema,
} from "../task";
import { TaskAssignmentSchema, TaskProfileGroupsSchema } from "./assignment";

const visibleToAll = { visibleTo: "*" } as const;

/**
 * Reasons a task can be parked on while it stays open. Persisted values are plain strings:
 * deprecate rather than delete, and never reuse a value with different semantics.
 */
export const TaskWaitingStatusSchema = z.enum([
  "awaiting_additional_data",
  "awaiting_response",
  "awaiting_documents",
  "awaiting_third_party_signature",
  "next_contact_scheduled",
  "awaiting_dvc",
  "awaiting_payment",
  "payment_in_progress",
  "payment_overdue",
  "payment_completed",
  "renegotiating",
]);

export const TaskTypeIdSchema = z
  .uuid()
  .describe("A task type's identity, stored on its tasks; unique across case types");

/** Where a task type applies: one case type, or client tasks. */
export const TaskScopeSchema = z.enum([...CaseType.options, "client"]);
export type TaskScope = z.infer<typeof TaskScopeSchema>;

/** Case steps creating a task can move its case to; the case leaves creation and finalizes otherwise. */
export const TaskStageSchema = CaseWorkflowStep.exclude(["creation", "finalized"]);

const ResolutionSchema = z
  .object({
    id: RequirementKeySchema.describe("Stable value stored in task.solution; never reused").meta(
      visibleToAll,
    ),
    label: z.string().trim().min(1).max(200).describe("Name shown to users").meta(visibleToAll),
    active: z
      .boolean()
      .describe("Inactive resolutions cannot be chosen but still label past tasks")
      .meta(visibleToAll),
    requiresNotes: z.boolean().describe("Completion needs a non-empty note").meta(visibleToAll),
    defaultNextTask: TaskTypeIdSchema.nullable()
      .describe("Next task preselected for this resolution; one of the next tasks")
      .meta(visibleToAll),
    finalizesCase: z
      .boolean()
      .describe("Completion finalizes the case once its tasks are created")
      .meta(visibleToAll),
    alsoCreate: z
      .array(TaskTypeIdSchema)
      .describe("Tasks always created alongside the next task")
      .meta(visibleToAll),
  })
  .strict()
  .describe("A completion outcome and what it does");
export type Resolution = z.infer<typeof ResolutionSchema>;

const editableFields = {
  name: z.string().trim().min(1).max(120).describe("Name shown to users").meta(visibleToAll),
  active: z
    .boolean()
    .describe("Inactive task types are no longer created; existing tasks stay operable")
    .meta(visibleToAll),
  automatedOnly: z
    .boolean()
    .describe("Only automation creates it; people never pick it")
    .meta(visibleToAll),
  secondary: z
    .boolean()
    .describe("Several may be open at once on the same parent")
    .meta(visibleToAll),
  collectsCompletionNotes: z
    .boolean()
    .describe("Completion shows and accepts notes")
    .meta(visibleToAll),
  emailAssignee: z
    .boolean()
    .describe("Whether assigning a task emails its new assignee a link to it"),
  waitingStatuses: z
    .array(TaskWaitingStatusSchema)
    .describe("Reasons a task of this type can wait on; empty when it never waits")
    .meta(visibleToAll),
  slaBusinessDays: z
    .number()
    .int()
    .nonnegative()
    .describe("Business days from first assignment to the deadline")
    .meta(visibleToAll),
  assignment: TaskAssignmentSchema.describe("Who receives a new task automatically"),
  allowedGroups: TaskProfileGroupsSchema.meta(visibleToAll),
  stage: TaskStageSchema.nullable()
    .describe("Step creating a task of this type moves its case to; null for tasks of any step")
    .meta(visibleToAll),
  analysis: z
    .boolean()
    .describe("Whether creating a task queues its code-owned AI analysis")
    .meta(visibleToAll),
  nextTasks: z
    .array(TaskTypeIdSchema)
    .describe("Task types of the same scope a user may choose to follow this task")
    .meta(visibleToAll),
  resolutions: z.array(ResolutionSchema).min(1).meta(visibleToAll),
  checklist: z.array(ChecklistItemSchema).meta(visibleToAll),
  groups: z.array(ChecklistGroupSchema).meta(visibleToAll),
};

export const TaskTypeSchema = z
  .object({
    id: TaskTypeIdSchema.meta(visibleToAll),
    scope: TaskScopeSchema.describe("The case type, or client tasks, it belongs to").meta(
      visibleToAll,
    ),
    system: SystemTaskKeySchema.nullable().meta(visibleToAll),
    ...editableFields,
    revision: z.number().int().positive().meta(visibleToAll),
    updatedBy: z.string().min(1),
    updatedAt: z.iso.datetime(),
  })
  .strict()
  .describe("A task type of one case type, or of client tasks: its name, flags and flow");
export type TaskType = z.infer<typeof TaskTypeSchema>;

/** A task type as validation sees it, without its save stamp. */
export type TaskTypeFields = Omit<TaskType, "revision" | "updatedBy" | "updatedAt">;

/** The flow part of a task type. */
export type TaskFlow = Pick<
  TaskType,
  "stage" | "analysis" | "nextTasks" | "resolutions" | "checklist" | "groups"
>;

export const TaskTypeListSchema = z.object({
  taskTypes: z.array(TaskTypeSchema).meta(visibleToAll),
});

export const SaveTaskTypeSchema = z
  .object({
    scope: TaskScopeSchema.describe("Fixed once created"),
    ...editableFields,
    expectedRevision: z.number().int().nonnegative().describe("0 creates the task type"),
  })
  .strict();
export type SaveTaskType = z.infer<typeof SaveTaskTypeSchema>;

export const TaskCatalogIssueSchema = z.object({
  taskType: z.string(),
  scope: TaskScopeSchema,
  path: z.array(z.union([z.string(), z.number()])),
  reason: z.string(),
});
export type TaskCatalogIssue = z.infer<typeof TaskCatalogIssueSchema>;

/** A minimal valid task type an admin starts from, completed through one resolution. */
export const blankTaskType = (
  scope: TaskScope,
  resolutionLabel: string,
): Omit<SaveTaskType, "expectedRevision"> => ({
  scope,
  name: "",
  active: true,
  automatedOnly: false,
  secondary: false,
  collectsCompletionNotes: true,
  emailAssignee: false,
  waitingStatuses: [],
  slaBusinessDays: 0,
  assignment: { mode: "unassigned" },
  allowedGroups: [],
  stage: null,
  analysis: false,
  nextTasks: [],
  resolutions: [
    {
      id: "completed",
      label: resolutionLabel,
      active: true,
      requiresNotes: false,
      defaultNextTask: null,
      finalizesCase: false,
      alsoCreate: [],
    },
  ],
  checklist: [],
  groups: [],
});
